import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, fmt, fileUrl } from "@/lib/api";
import { usePanel } from "./PanelContext";

const EMPTY = { name: "", price: "", description: "", category_id: "none", image: null, available: true };

function ProductDialog({ open, onOpenChange, initial, cats, onSaved }) {
  const { api } = usePanel();
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  useEffect(() => { if (open) setF(initial ? { ...initial, category_id: initial.category_id || "none" } : EMPTY); }, [open, initial]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setF((x) => ({ ...x, image: data.path }));
      toast.success("Fotoğraf yüklendi");
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!f.name || f.price === "") return toast.error("Ad ve fiyat zorunlu");
    const body = { name: f.name, price: parseFloat(f.price), description: f.description, category_id: f.category_id === "none" ? null : f.category_id, image: f.image, available: f.available };
    try {
      if (initial) await api.put(`/products/${initial.id}`, body); else await api.post("/products", body);
      toast.success("Ürün kaydedildi"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-dialog">
        <DialogHeader><DialogTitle>{initial ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
        <button type="button" onClick={() => fileRef.current.click()} className="h-40 rounded-2xl border-2 border-dashed grid place-items-center overflow-hidden bg-slate-50 hover:border-[#FF6B0A]" data-testid="product-image-upload-btn">
          {f.image ? <img src={fileUrl(f.image)} alt="" className="h-full w-full object-cover" /> : <span className="text-sm text-slate-500 flex flex-col items-center gap-1"><ImagePlus />{uploading ? "Yükleniyor..." : "Menü fotoğrafı ekle"}</span>}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="product-image-input" />
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Ürün adı</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-name-input" /></div>
          <div><Label>Fiyat (TL)</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="product-price-input" /></div>
          <div><Label>Kategori</Label>
            <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v })}>
              <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Kategorisiz</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Açıklama</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="product-desc-input" /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.available} onCheckedChange={(v) => setF({ ...f, available: v })} data-testid="product-available-switch" />Satışta</label>
        </div>
        <Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={save} disabled={uploading} data-testid="product-save-btn">Kaydet</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function MenuPage() {
  const { api } = usePanel();
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [catName, setCatName] = useState("");
  const [active, setActive] = useState("all");
  const [dlg, setDlg] = useState({ open: false, item: null });

  const load = () => Promise.all([api.get("/categories"), api.get("/products")]).then(([c, p]) => { setCats(c.data); setProducts(p.data); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addCat = async () => {
    if (!catName.trim()) return;
    try { await api.post("/categories", { name: catName, sort: cats.length }); setCatName(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delCat = async (c) => { if (!window.confirm(`${c.name} kategorisi silinsin mi?`)) return; try { await api.delete(`/categories/${c.id}`); setActive("all"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const delProd = async (p) => { if (!window.confirm(`${p.name} silinsin mi?`)) return; try { await api.delete(`/products/${p.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const shown = active === "all" ? products : products.filter((p) => p.category_id === active);

  return (
    <div className="space-y-6 rise" data-testid="menu-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto"><h1 className="text-3xl font-extrabold">Menü Yönetimi</h1><p className="text-sm text-slate-500">İstediğiniz kategori ve ürünü fotoğrafıyla ekleyin.</p></div>
        <Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-1" onClick={() => setDlg({ open: true, item: null })} data-testid="menu-add-product-btn"><Plus className="h-4 w-4" />Ürün Ekle</Button>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <div className="rounded-3xl bg-white border p-4 h-fit space-y-2">
          <p className="font-display font-bold px-1">Kategoriler</p>
          <button onClick={() => setActive("all")} className={`w-full text-left rounded-xl px-3 py-2 text-sm ${active === "all" ? "bg-[#0F1F45] text-white" : "hover:bg-slate-100"}`} data-testid="menu-cat-all">Tüm ürünler ({products.length})</button>
          {cats.map((c) => (
            <div key={c.id} className={`flex items-center rounded-xl ${active === c.id ? "bg-[#0F1F45] text-white" : "hover:bg-slate-100"}`}>
              <button onClick={() => setActive(c.id)} className="flex-1 text-left px-3 py-2 text-sm" data-testid={`menu-cat-${c.id}`}>{c.name}</button>
              <button onClick={() => delCat(c)} className="px-2 opacity-60 hover:opacity-100" data-testid={`menu-cat-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <Input placeholder="Yeni kategori" value={catName} onChange={(e) => setCatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} data-testid="menu-cat-name-input" />
            <Button size="icon" onClick={addCat} data-testid="menu-cat-add-btn"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 h-fit">
          {shown.map((p) => (
            <div key={p.id} className="rounded-3xl bg-white border overflow-hidden group" data-testid={`product-card-${p.id}`}>
              <div className="h-40 bg-slate-100 overflow-hidden">{p.image && <img src={fileUrl(p.image)} alt={p.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />}</div>
              <div className="p-4">
                <div className="flex items-start gap-2"><p className="font-semibold flex-1">{p.name}</p><p className="font-bold text-[#FF6B0A]">{fmt(p.price)}</p></div>
                <p className="text-xs text-slate-500 line-clamp-2 mt-1 min-h-[2rem]">{p.description}</p>
                <div className="flex items-center gap-2 mt-3">
                  <span className={`text-xs font-semibold ${p.available ? "text-emerald-600" : "text-slate-400"}`}>{p.available ? "Satışta" : "Kapalı"}</span>
                  <Button size="icon" variant="ghost" className="ml-auto" onClick={() => setDlg({ open: true, item: p })} data-testid={`product-edit-btn-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => delProd(p)} data-testid={`product-delete-btn-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
          {!shown.length && <div className="col-span-full rounded-3xl border border-dashed p-12 text-center text-slate-500">Bu kategoride ürün yok.</div>}
        </div>
      </div>
      <ProductDialog open={dlg.open} initial={dlg.item} cats={cats} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={load} />
    </div>
  );
}
